import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiUploadCloud, FiFile, FiMoreVertical, FiDownload, FiEye, FiTrash2, 
  FiEdit, FiShare2, FiFolder, FiSearch, FiX, FiFileText, FiImage, 
  FiZoomIn, FiZoomOut, FiMaximize2, FiChevronLeft, FiChevronRight,
  FiLock, FiClock, FiUser
} = FiIcons;

const mockDocuments = [
  { 
    id: 1, 
    name: 'Treatment_Plan_v1.pdf', 
    category: 'Treatment Plans', 
    uploader: 'Dr. Emily Carter', 
    date: '2024-07-21', 
    version: '1.0', 
    status: 'Approved', 
    size: '2.5 MB', 
    encrypted: true,
    type: 'pdf',
    preview: 'https://images.unsplash.com/photo-1568667256549-094345857637?w=400'
  },
  { 
    id: 2, 
    name: 'pano_xray_01.jpg', 
    category: 'Imaging & X-rays', 
    uploader: 'Tech John Doe', 
    date: '2024-07-20', 
    version: '1.0', 
    status: 'Final', 
    size: '5.1 MB', 
    encrypted: true,
    type: 'image',
    preview: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=400'
  },
  { 
    id: 3, 
    name: 'lab_results_biopsy.docx', 
    category: 'Lab Results', 
    uploader: 'LabCorp', 
    date: '2024-07-19', 
    version: '1.0', 
    status: 'Review', 
    size: '800 KB', 
    encrypted: true,
    type: 'doc',
    preview: null
  },
  { 
    id: 4, 
    name: 'Consultation_Notes_v2.pdf', 
    category: 'Consultation Notes', 
    uploader: 'Dr. Alan Grant', 
    date: '2024-07-18', 
    version: '2.0', 
    status: 'Draft', 
    size: '1.2 MB', 
    encrypted: false,
    type: 'pdf',
    preview: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400'
  },
  { 
    id: 5, 
    name: 'Financial_Agreement.pdf', 
    category: 'Consent & Financial Docs', 
    uploader: 'Admin Staff', 
    date: '2024-07-17', 
    version: '1.0', 
    status: 'Final', 
    size: '600 KB', 
    encrypted: true,
    type: 'pdf',
    preview: 'https://images.unsplash.com/photo-1554224311-beee460201e8?w=400'
  },
];

const DocumentLibrary = ({ dealId }) => {
  const [documents, setDocuments] = useState(mockDocuments);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [previewDocument, setPreviewDocument] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const fileInputRef = useRef(null);

  const categories = ['All', ...new Set(documents.map(d => d.category))];

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      console.log('Uploading files:', files);
      setIsUploading(false);
    }
  };

  const handlePreview = (doc) => {
    setPreviewDocument(doc);
    setZoomLevel(100);
    setActiveDropdown(null);
  };

  const handleNextDocument = () => {
    const currentIndex = filteredDocuments.findIndex(d => d.id === previewDocument.id);
    if (currentIndex < filteredDocuments.length - 1) {
      setPreviewDocument(filteredDocuments[currentIndex + 1]);
      setZoomLevel(100);
    }
  };

  const handlePreviousDocument = () => {
    const currentIndex = filteredDocuments.findIndex(d => d.id === previewDocument.id);
    if (currentIndex > 0) {
      setPreviewDocument(filteredDocuments[currentIndex - 1]);
      setZoomLevel(100);
    }
  };

  const StatusBadge = ({ status }) => {
    const statusClasses = {
      Approved: 'bg-green-100 text-green-800',
      Final: 'bg-blue-100 text-blue-800',
      Review: 'bg-yellow-100 text-yellow-800',
      Draft: 'bg-gray-100 text-gray-800',
    };
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusClasses[status] || ''}`}>{status}</span>;
  };

  const getFileIcon = (type) => {
    switch(type) {
      case 'image': return FiImage;
      case 'pdf': return FiFileText;
      default: return FiFile;
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || doc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div>
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Document Library</h3>
          <p className="text-sm text-gray-500 mt-1">{filteredDocuments.length} documents</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
          <div className="relative flex-1 sm:flex-initial">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full sm:w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsUploading(true)}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors whitespace-nowrap"
          >
            <SafeIcon icon={FiUploadCloud} />
            Upload
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredDocuments.map((doc) => (
          <motion.div
            key={doc.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -4 }}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all cursor-pointer group"
            onClick={() => handlePreview(doc)}
          >
            <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
              {doc.preview ? (
                <img src={doc.preview} alt={doc.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              ) : (
                <SafeIcon icon={getFileIcon(doc.type)} className="w-16 h-16 text-gray-400" />
              )}
              {doc.encrypted && (
                <div className="absolute top-2 right-2 bg-green-500 text-white p-1.5 rounded-full">
                  <SafeIcon icon={FiLock} className="w-3 h-3" />
                </div>
              )}
              <div className="absolute top-2 left-2">
                <StatusBadge status={doc.status} />
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-semibold text-gray-900 truncate mb-2 group-hover:text-blue-600 transition-colors">{doc.name}</h4>
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                <SafeIcon icon={FiFolder} className="w-3 h-3" />
                <span className="truncate">{doc.category}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiUser} className="w-3 h-3" />
                  <span className="truncate">{doc.uploader.split(' ')[0]}</span>
                </div>
                <div className="flex items-center gap-1">
                  <SafeIcon icon={FiClock} className="w-3 h-3" />
                  <span>{doc.date}</span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">{doc.size}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === doc.id ? null : doc.id);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                >
                  <SafeIcon icon={FiMoreVertical} className="w-4 h-4" />
                </button>
              </div>
            </div>
            <AnimatePresence>
              {activeDropdown === doc.id && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -10 }}
                  className="absolute right-4 mt-2 w-48 bg-white rounded-lg shadow-xl z-10 border border-gray-200 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button 
                    onClick={() => handlePreview(doc)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors"
                  >
                    <SafeIcon icon={FiEye} className="w-4 h-4" /> Preview
                  </button>
                  <button className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors">
                    <SafeIcon icon={FiDownload} className="w-4 h-4" /> Download
                  </button>
                  <button className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors">
                    <SafeIcon icon={FiShare2} className="w-4 h-4" /> Share
                  </button>
                  <button className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left transition-colors">
                    <SafeIcon icon={FiEdit} className="w-4 h-4" /> Rename
                  </button>
                  <div className="border-t border-gray-100"></div>
                  <button className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left transition-colors">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-16 text-gray-500">
          <SafeIcon icon={FiSearch} className="mx-auto h-16 w-16 opacity-50 mb-4" />
          <p className="text-lg font-medium">No documents found</p>
          <p className="text-sm mt-1">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Upload Modal */}
      <AnimatePresence>
        {isUploading && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsUploading(false)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Upload Documents</h3>
                <button 
                  onClick={() => setIsUploading(false)} 
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>
              <div className="p-8">
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-all group"
                  onClick={handleUploadClick}
                >
                  <SafeIcon icon={FiUploadCloud} className="mx-auto h-16 w-16 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <p className="mt-4 text-sm text-gray-600">
                    <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 mt-2">PDF, DOCX, JPG, PNG, etc. (max 25MB)</p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <div className="mt-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option>Treatment Plans</option>
                    <option>Imaging & X-rays</option>
                    <option>Lab Results</option>
                    <option>Consultation Notes</option>
                    <option>Consent & Financial Docs</option>
                  </select>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
                <button 
                  onClick={() => setIsUploading(false)} 
                  className="py-2 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button className="py-2 px-5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                  Upload Files
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewDocument && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewDocument(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }} 
              animate={{ scale: 1, opacity: 1 }} 
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <SafeIcon icon={getFileIcon(previewDocument.type)} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-gray-800 truncate">{previewDocument.name}</h3>
                    <p className="text-sm text-gray-500">{previewDocument.category} • {previewDocument.size}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setZoomLevel(Math.max(50, zoomLevel - 25))}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    disabled={zoomLevel <= 50}
                  >
                    <SafeIcon icon={FiZoomOut} className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-medium text-gray-600 w-16 text-center">{zoomLevel}%</span>
                  <button 
                    onClick={() => setZoomLevel(Math.min(200, zoomLevel + 25))}
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                    disabled={zoomLevel >= 200}
                  >
                    <SafeIcon icon={FiZoomIn} className="w-5 h-5" />
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-2"></div>
                  <button className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setPreviewDocument(null)} 
                    className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Preview Content */}
              <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center p-8">
                {previewDocument.preview ? (
                  <img 
                    src={previewDocument.preview} 
                    alt={previewDocument.name} 
                    style={{ transform: `scale(${zoomLevel / 100})` }}
                    className="max-w-full max-h-full object-contain transition-transform rounded-lg shadow-lg"
                  />
                ) : (
                  <div className="text-center">
                    <SafeIcon icon={getFileIcon(previewDocument.type)} className="w-24 h-24 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Preview not available</p>
                    <p className="text-sm text-gray-500 mt-2">Download the file to view its contents</p>
                  </div>
                )}
              </div>

              {/* Navigation Footer */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <button
                  onClick={handlePreviousDocument}
                  disabled={filteredDocuments.findIndex(d => d.id === previewDocument.id) === 0}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
                  Previous
                </button>
                <div className="text-sm text-gray-600">
                  {filteredDocuments.findIndex(d => d.id === previewDocument.id) + 1} of {filteredDocuments.length}
                </div>
                <button
                  onClick={handleNextDocument}
                  disabled={filteredDocuments.findIndex(d => d.id === previewDocument.id) === filteredDocuments.length - 1}
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentLibrary;